import { FunctionComponent, useEffect, useState } from 'react';
import { ProductConfiguration } from '@/models/product';
import {
    CACHE_KEY_BULK_UPDATES,
    usePostBulkUpdatesMutation,
} from '@/api/v2/productConfigurations';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import {
    BulkUpdateActionPayload,
    BulkUpdateType,
    ConfigurationCollection,
    ConfigurationState,
    initialConfigurationState,
} from '@/models/bulkUpdate';
import { useGetLiftOperatorQuery } from '@/api/v2/liftOperator';
import { DateRange } from '@/models/general';
import { useConfigurationPanelContext } from '@/pages/productConfiguration/components/ProductConfigurationBulkUpdate/ConfigurationPanelContext';
import { ConfigurationsPanel } from '@/pages/productConfiguration/components/ProductConfigurationBulkUpdate/ConfigurationsPanel';

const plainIntegrationObject = () => ({
    barcode: false,
    internalProduction: false,
    keycard: false,
    pickup: false,
    mobileFlow: false,
});

export const initialBookingOptions = {
    integrationConfiguration: plainIntegrationObject(),
    portalConfiguration: plainIntegrationObject(),
    b2bConfiguration: plainIntegrationObject(),
    hospitalityConfiguration: plainIntegrationObject(),
};

type ConfigurationsPanelContainerProps = {
    selectedConfigurations: ProductConfiguration[];
};

export const ConfigurationsPanelContainer: FunctionComponent<
    ConfigurationsPanelContainerProps
> = ({ selectedConfigurations }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();

    // prefetch for components inside panel
    useGetLiftOperatorQuery({
        liftOperatorId: liftOperatorId,
    });

    const { addNotification } = useNotification();
    const [postBulkUpdate, { isLoading }] = usePostBulkUpdatesMutation({
        fixedCacheKey: CACHE_KEY_BULK_UPDATES,
    });
    const [configurationState, setConfigurationState] =
        useState<ConfigurationState>(initialConfigurationState);
    const [bookingConfiguration, setBookingConfiguration] =
        useState<ConfigurationCollection>(null);
    const [validityConfiguration, setValidityConfiguration] =
        useState<DateRange>(null);
    const [startDateValidityConfiguration, setStartDateValidityConfiguration] =
        useState<DateRange>(null);
    const [selectedConditionLinkIds, setSelectedConditionLinkIds] = useState<
        string[]
    >([]);
    const [removeConditions, setRemoveConditions] = useState<boolean>(null);
    const [isActive, setIsActive] = useState<boolean>(null);

    const { setIsActive: setPanelActive } = useConfigurationPanelContext();

    const onPanelChange = (action: BulkUpdateActionPayload) => {
        switch (action.type) {
            case BulkUpdateType.ACTIVE_STATUS:
                setIsActive(action.isActive);
                break;
            case BulkUpdateType.SET_DYNAMIC_PRICING:
                setSelectedConditionLinkIds(action.conditionLinkIds);
                break;
            case BulkUpdateType.SET_REMOVE_CONDITION:
                setRemoveConditions(action.payload);
                break;
            case BulkUpdateType.SET_BOOKING_OPTIONS:
                setBookingConfiguration(action.configurations);
                break;
            case BulkUpdateType.SET_VALIDITY_OPTIONS:
                setValidityConfiguration(action.payload);
                break;
            case BulkUpdateType.SET_START_DATE_VALIDITY_OPTIONS:
                setStartDateValidityConfiguration(action.payload);
                break;
        }
    };

    const onSave = () => {
        postBulkUpdate({
            productIds: selectedConfigurations.map((sC) => sC.productId),
            liftOperatorId: liftOperatorId,
            enabled: isActive,
            conditionLinkIds: selectedConditionLinkIds,
            removeConditionLinks: removeConditions,
            ...(bookingConfiguration ? bookingConfiguration : {}),
            validFrom: validityConfiguration?.[0] || null,
            validTo: validityConfiguration?.[1] || null,
            startDateValidFrom: startDateValidityConfiguration?.[0] || null,
            startDateValidTo: startDateValidityConfiguration?.[1] || null,
            removeValidFromTo: configurationState.setRemoveValidFromTo,
            removeStartDateFromTo:
                configurationState.setRemoveStartDateValidFromTo,
        })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Konfigurationen erfolgreich aktualisiert',
                });
                setConfigurationState(initialConfigurationState);
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Aktualisieren. Versuchen Sie es später noch einmal',
                });
            });
    };

    useEffect(() => {
        setPanelActive(!!selectedConfigurations?.length);
    }, [selectedConfigurations]);

    return (
        <ConfigurationsPanel
            isActive={isActive}
            removeConditions={removeConditions}
            selectedConditionLinkIds={selectedConditionLinkIds}
            numOfConfigurations={selectedConfigurations.length}
            configurationCollection={bookingConfiguration}
            onConfigurationsChange={onPanelChange}
            configurationState={configurationState}
            onConfigurationStateChange={(nextState) => {
                setConfigurationState(nextState);
            }}
            validityRange={validityConfiguration || [null, null]}
            startDateValidityRange={
                startDateValidityConfiguration || [null, null]
            }
            onSave={onSave}
            isLoading={isLoading}
        />
    );
};
